import {ActionBar as GitHubUiActionBar, type ActionBarProps as GitHubUiActionBarProps} from '@github-ui/action-bar'
import {testIdProps} from '@github-ui/test-id-props'
import {ActionBar as PrimerActionBar} from '@primer/react'
import {clsx} from 'clsx'
import type {
  ComponentProps,
  HTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  ReactNode,
  Ref,
  RefObject,
} from 'react'
import {useEffect, useRef} from 'react'

import densityStyles from '../density-gap.module.css'
import type {StylableProps} from '../types'
import styles from './ActionBar.module.css'
import type {ListViewDensityToggle} from './DensityToggle'

const inlineAnchorAttribute = 'data-action-bar-picker-anchor'
const overflowAnchorSelector = ':scope > [role="toolbar"] > button[data-component="IconButton"]'

type ActionBarButtonAnchorOptions = Omit<
  ComponentProps<typeof PrimerActionBar.Button>,
  'children' | 'onClick' | 'ref'
> & {
  children: ReactNode
}

type AnchorRenderProps = HTMLAttributes<HTMLElement> & {ref?: Ref<HTMLButtonElement>}

export type ActionBarButtonAnchor = (
  props: AnchorRenderProps,
  ref?: RefObject<HTMLButtonElement | null>,
) => React.ReactElement

function getRefValue(ref: Ref<HTMLButtonElement> | undefined) {
  return typeof ref === 'object' && ref ? ref.current : null
}

function setRefValue(ref: Ref<HTMLButtonElement> | undefined, value: HTMLButtonElement | null) {
  if (typeof ref === 'function') ref(value)
  else if (ref) ref.current = value
}

function getAnchorRefs(...refs: Array<Ref<HTMLButtonElement> | undefined>) {
  return refs.filter((ref, index): ref is Ref<HTMLButtonElement> => Boolean(ref) && refs.indexOf(ref) === index)
}

type ActionBarButtonAnchorProps = ActionBarButtonAnchorOptions & {
  anchorProps: AnchorRenderProps
  anchorRef?: RefObject<HTMLButtonElement | null>
}

function ActionBarButtonAnchor({anchorProps, anchorRef, children, ...buttonProps}: ActionBarButtonAnchorProps) {
  const {onClick, onKeyDown, ref: renderAnchorRef, ...restAnchorProps} = anchorProps
  const visibleAnchorRef = useRef<HTMLButtonElement | null>(null)
  const open = anchorProps['aria-expanded'] === true
  const wasOpenRef = useRef(open)

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Primer snapshots return focus when the overlay opens, before responsive overflow can change the visible trigger.
    if (wasOpenRef.current && !open) visibleAnchorRef.current?.focus()
    wasOpenRef.current = open
  }, [open])

  const setInlineAnchor = (element: HTMLButtonElement | null) => {
    const anchorRefs = getAnchorRefs(anchorRef, renderAnchorRef)
    if (!element) {
      visibleAnchorRef.current = null
      for (const ref of anchorRefs) setRefValue(ref, null)
      return
    }

    const actionBar = element.closest('[data-component="ActionBar"]')
    const syncAnchor = () => {
      const overflowAnchor = actionBar?.querySelector<HTMLButtonElement>(overflowAnchorSelector)
      const nextAnchor = element.hasAttribute('data-overflowing') ? (overflowAnchor ?? element) : element
      visibleAnchorRef.current = nextAnchor
      for (const ref of anchorRefs) setRefValue(ref, nextAnchor)
    }

    syncAnchor()
    if (!actionBar) return

    const observer = new MutationObserver(syncAnchor)
    observer.observe(actionBar, {
      attributes: true,
      attributeFilter: ['data-overflowing', 'data-more-button-inactive'],
      subtree: true,
    })

    return () => {
      observer.disconnect()
      visibleAnchorRef.current = null
      for (const ref of anchorRefs) setRefValue(ref, null)
    }
  }

  const handleClick = (event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
    const anchorRefs = getAnchorRefs(anchorRef, renderAnchorRef)
    const currentAnchor = anchorRefs.map(getRefValue).find(Boolean)
    const actionBar = currentAnchor?.closest('[data-component="ActionBar"]')
    const nextAnchor = event.currentTarget.hasAttribute(inlineAnchorAttribute)
      ? (event.currentTarget as HTMLButtonElement)
      : (actionBar?.querySelector<HTMLButtonElement>(overflowAnchorSelector) ?? currentAnchor ?? null)
    visibleAnchorRef.current = nextAnchor
    for (const ref of anchorRefs) setRefValue(ref, nextAnchor)

    if ('key' in event) queueMicrotask(() => onKeyDown?.(event))
    else onClick?.(event)
  }

  return (
    <PrimerActionBar.Button
      {...buttonProps}
      {...restAnchorProps}
      ref={setInlineAnchor}
      data-action-bar-picker-anchor=""
      onClick={handleClick}
      onKeyDown={onKeyDown}
    >
      {children}
    </PrimerActionBar.Button>
  )
}

export function createActionBarButtonAnchor(options: ActionBarButtonAnchorOptions) {
  return function ActionBarButtonAnchorRenderer(
    anchorProps: AnchorRenderProps,
    anchorRef?: RefObject<HTMLButtonElement | null>,
  ) {
    return <ActionBarButtonAnchor {...options} anchorProps={anchorProps} anchorRef={anchorRef} />
  }
}

// Require actionsLabel when either action type is provided and prevent mixing the two action APIs.
type ListViewActionBar =
  | {
      /**
       * Description of the controls found in the action bar, e.g., "Issue actions", "Markdown formatting tools".
       * Used as the action bar's accessible name. Required when `actions` or `actionBarItems` is provided.
       */
      actionsLabel: GitHubUiActionBarProps['label']
      /**
       * The controls to show in the action bar. These will move into a dropdown menu when the screen isn't big enough to
       * fit them side by side.
       */
      actions: GitHubUiActionBarProps['actions']
      actionBarItems?: never
    }
  | {
      /**
       * Primer ActionBar items that delegate responsive overflow behavior to Primer.
       */
      actionBarItems: ReactNode
      actionsLabel: GitHubUiActionBarProps['label']
      actions?: never
    }
  | {actionsLabel?: never; actions?: never; actionBarItems?: never}

export type ListViewActionBarProps = ListViewActionBar & {
  /**
   * Include a toggle to switch between Comfortable and Condensed view modes.
   */
  densityToggle?: ReactElement<typeof ListViewDensityToggle>
} & StylableProps &
  Omit<GitHubUiActionBarProps, 'actions' | 'label'>

export const ListViewActionBar = ({
  actions,
  actionBarItems,
  style,
  className,
  children,
  density,
  actionsLabel,
  densityToggle,
  overflowMenuProps,
  overflowMenuToggleProps,
  anchorRef,
}: ListViewActionBarProps & Pick<GitHubUiActionBarProps, 'overflowMenuProps' | 'overflowMenuToggleProps'>) => {
  const nonCollapsibleActions = (
    <>
      {children}
      {densityToggle}
    </>
  )

  if (actionBarItems !== undefined) {
    return (
      <div {...testIdProps('list-view-actions')} className={clsx(styles.container, className)} style={style}>
        <PrimerActionBar aria-label={actionsLabel.trim()} flush gap={density === 'none' ? 'none' : 'condensed'}>
          {actionBarItems}
        </PrimerActionBar>
        {nonCollapsibleActions}
      </div>
    )
  }

  if (actions && actions.length > 0) {
    return (
      <GitHubUiActionBar
        label={actionsLabel.trim()}
        actions={actions}
        density={density}
        overflowMenuProps={overflowMenuProps}
        overflowMenuToggleProps={overflowMenuToggleProps}
        anchorRef={anchorRef}
        {...testIdProps('list-view-actions')}
        style={style}
        className={className}
      >
        {nonCollapsibleActions}
      </GitHubUiActionBar>
    )
  }

  return (
    <div
      {...testIdProps('list-view-actions')}
      style={style}
      className={clsx(styles.container, density && densityStyles[density], className)}
    >
      {nonCollapsibleActions}
    </div>
  )
}
